/**
 * Phase 3G: Export Package Panel
 * 
 * Final step in the closed-loop revenue workflow.
 * Shows hard gate checklist, format selection, and export generation.
 * 
 * HARD BOUNDARIES:
 * - Production: export blocked if any gate fails
 * - Demo: export preview always allowed with clear "Illustrative Only" label
 * - LINK does NOT submit claims — export only
 */

import React, { useState, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ShieldCheck,
  ShieldAlert,
  Copy,
  Download,
  FileText,
  FileJson,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Package,
  Lock,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { toast } from 'sonner';
import {
  runExportGates,
  buildGateInputFromWorkflow,
  type ExportGateResult,
  type ExportGateCheck,
  type ExportGateQuickAction,
} from '@/lib/export/exportGateEngine';
import {
  generateEpicCopyPaste,
  generatePdfSummary,
  generateJsonExport,
  type ExportPackageInput,
  type ExportCptLine,
  type ExportAuditEvent,
} from '@/lib/export/exportPackageGenerator';
import type { FinalCptLine } from '@/hooks/useFinalCptSequencing';
import type { SequencingResult } from '@/lib/phase3/cptSequencingEngine';

// ============================================================================
// PROPS
// ============================================================================

/** Allowed export roles — surgeon can view but not export unless explicitly permitted */
type ExportRole = 'link_ops' | 'surgeon' | 'hospital_admin';

interface ExportPackagePanelProps {
  caseId: string | null;
  caseNumber?: string;
  authReference?: string;
  surgeonName?: string;
  facilityName?: string;
  scheduledDate?: string;
  attestationText?: string;
  attestationTimestamp?: string;
  /** Final sequenced lines from Phase 3F */
  finalLines: FinalCptLine[];
  sequencingResult: SequencingResult | null;
  /** CPT states from clarification workflow */
  cptStates: Record<string, {
    surgeonConfirmation: string | null;
    billableStatus: string;
    documentationStatus?: string;
  }>;
  authorizedCpts: string[];
  cptDescriptions?: Record<string, string>;
  hasOpenPromptsForCpt: (cptCode: string) => boolean;
  analysisComplete: boolean;
  /** Current post_op_state from the case — export blocked unless 'export_ready' or demo */
  postOpState?: string;
  /** Current user role — controls export access */
  userRole?: ExportRole;
  /** Audit timeline events */
  auditTimeline?: ExportAuditEvent[];
  /** Quick action callbacks */
  onOpenClarifications?: (cptCode: string) => void;
  onOpenConfirmation?: (cptCode: string) => void;
  onViewAuditLog?: () => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ExportPackagePanel({
  caseId,
  caseNumber = 'CASE-UNKNOWN',
  authReference,
  surgeonName,
  facilityName,
  scheduledDate,
  attestationText,
  attestationTimestamp,
  finalLines,
  sequencingResult,
  cptStates,
  authorizedCpts,
  cptDescriptions = {},
  hasOpenPromptsForCpt,
  analysisComplete,
  postOpState,
  userRole = 'link_ops',
  auditTimeline = [],
  onOpenClarifications,
  onOpenConfirmation,
  onViewAuditLog,
}: ExportPackagePanelProps) {
  const { isDemoMode } = useDemoMode();
  const [selectedFormat, setSelectedFormat] = useState<'epic' | 'pdf' | 'json'>('epic');
  const [gateListOpen, setGateListOpen] = useState(false);
  const [epicOutput, setEpicOutput] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // ── Role-based access: only link_ops can generate exports ──────
  const canUserExport = userRole === 'link_ops' || userRole === 'hospital_admin';
  const isViewOnly = userRole === 'surgeon';

  // ── Post-op state gate: production requires EXPORT_READY ───────
  const isPostOpStateValid = isDemoMode || postOpState === 'export_ready' || !postOpState;

  // ── Run gate checks ──────────────────────────────────────────────
  const gateResult = useMemo<ExportGateResult>(() => {
    const gateInput = buildGateInputFromWorkflow({
      cptStates,
      authorizedCpts,
      sequencedLines: finalLines.map(l => ({
        cptCode: l.cptCode,
        reviewRequired: l.reviewRequired,
        reviewReasons: l.reviewReasons,
        modifiers: l.modifiers,
      })),
      hasSurgeonAttestation: !!attestationText || isDemoMode,
      attestationTimestamp,
      hasOpenPromptsForCpt,
      analysisComplete,
      isDemoMode,
    });

    return runExportGates(gateInput);
  }, [cptStates, authorizedCpts, finalLines, attestationText, attestationTimestamp, hasOpenPromptsForCpt, analysisComplete, isDemoMode]);

  // ── Build export input ───────────────────────────────────────────
  const exportInput = useMemo<ExportPackageInput>(() => {
    const cptLines: ExportCptLine[] = finalLines.map(l => ({
      sequence: l.sequence,
      cptCode: l.cptCode,
      cptDescription: cptDescriptions[l.cptCode] || l.cptCode,
      modifiers: l.modifiers,
      units: l.units,
      finalCodeString: l.finalCodeString,
      modifierExplanations: l.modifierExplanations,
    }));

    return {
      caseNumber,
      authReference,
      surgeonName: isDemoMode ? 'Dr. Demo Surgeon' : surgeonName,
      facilityName: isDemoMode ? 'Demo Facility' : facilityName,
      scheduledDate,
      attestationText: isDemoMode 
        ? 'I attest that the selected procedures represent clinically possible intra-operative findings for this case.'
        : attestationText,
      attestationTimestamp: isDemoMode ? new Date().toISOString() : attestationTimestamp,
      cptLines,
      auditTimeline,
      isDemoExport: isDemoMode,
      generatedAt: new Date().toISOString(),
    };
  }, [finalLines, caseNumber, authReference, surgeonName, facilityName, scheduledDate, attestationText, attestationTimestamp, auditTimeline, isDemoMode, cptDescriptions]);

  // ── Log export event to audit trail ───────────────────────────
  const logExportEvent = useCallback(async (format: string, success: boolean) => {
    if (isDemoMode || !caseId) return; // Don't log demo exports
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('authorization_activity_log').insert({
        authorization_id: caseId,
        action: success ? 'export_generated' : 'export_failed',
        actor_id: user?.id || 'system',
        actor_type: userRole || 'link_ops',
        details: {
          format,
          caseNumber,
          cptCount: finalLines.length,
          gatesPassed: gateResult.passedCount,
          gatesTotal: gateResult.totalCount,
          timestamp: new Date().toISOString(),
        } as any,
      });
    } catch (err) {
      console.error('Failed to log export event:', err);
    }
  }, [isDemoMode, caseId, userRole, caseNumber, finalLines.length, gateResult]);

  // ── Generate export ──────────────────────────────────────────────
  const handleGenerate = useCallback(async () => {
    // Role gate: surgeons cannot generate exports
    if (isViewOnly) {
      toast.error('Export not permitted', { description: 'Surgeons can view export summaries but cannot generate exports.' });
      return;
    }

    // Post-op state gate
    if (!isPostOpStateValid) {
      toast.error('Export blocked', { description: `Case must reach "Export Ready" state before export. Current state: ${postOpState}` });
      return;
    }

    if (!gateResult.canExport) {
      toast.error('Export blocked', { description: gateResult.summaryMessage });
      return;
    }

    setIsGenerating(true);

    try {
      if (selectedFormat === 'epic') {
        const output = generateEpicCopyPaste(exportInput);
        setEpicOutput(output);
        await logExportEvent('epic_copy_paste', true);
        toast.success('Epic copy/paste block generated');
      } else if (selectedFormat === 'pdf') {
        const doc = generatePdfSummary(exportInput);
        const filename = isDemoMode 
          ? `LINK_DEMO_${caseNumber}_export.pdf`
          : `LINK_${caseNumber}_export.pdf`;
        doc.save(filename);
        await logExportEvent('pdf_summary', true);
        toast.success('PDF downloaded', { description: filename });
      } else if (selectedFormat === 'json') {
        const payload = generateJsonExport(exportInput);
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = isDemoMode 
          ? `LINK_DEMO_${caseNumber}_export.json`
          : `LINK_${caseNumber}_export.json`;
        a.click();
        URL.revokeObjectURL(url);
        await logExportEvent('json', true);
        toast.success('JSON export downloaded');
      }
    } catch (err) {
      console.error('Export generation failed:', err);
      await logExportEvent(selectedFormat, false);
      toast.error('Unable to generate export package. Please try again or contact support.');
    } finally {
      setIsGenerating(false);
    }
  }, [gateResult, selectedFormat, exportInput, caseNumber, isDemoMode, isViewOnly, isPostOpStateValid, postOpState, logExportEvent]);

  // ── Copy to clipboard ────────────────────────────────────────────
  const handleCopyEpic = useCallback(() => {
    if (epicOutput) {
      navigator.clipboard.writeText(epicOutput);
      toast.success('Copied to clipboard');
    }
  }, [epicOutput]);

  // ── Quick action handler ─────────────────────────────────────────
  const handleQuickAction = useCallback((action: ExportGateQuickAction) => {
    switch (action.type) {
      case 'open_clarifications':
        onOpenClarifications?.(action.cptCode);
        break;
      case 'open_confirmation':
        onOpenConfirmation?.(action.cptCode);
        break;
      case 'view_audit_log':
        onViewAuditLog?.();
        break;
      case 'resolve_modifier_conflict':
        // Scroll to sequencing panel
        toast.info(`Resolve modifier conflicts for CPT ${action.cptCode} in the sequencing panel above`);
        break;
      case 'open_attestation':
        toast.info('Navigate to the authorization wizard to complete attestation');
        break;
    }
  }, [onOpenClarifications, onOpenConfirmation, onViewAuditLog]);

  // ── Gate progress ────────────────────────────────────────────────
  const gateProgress = gateResult.totalCount > 0 
    ? (gateResult.passedCount / gateResult.totalCount) * 100 
    : 0;

  const blockingGates = gateResult.gates.filter(g => g.severity === 'blocking');
  const auditGates = gateResult.gates.filter(g => g.closedLoopStage === 'audit');

  return (
    <TooltipProvider>
      <Card className={cn(
        "border-2",
        gateResult.canExport ? "border-success/50" : "border-destructive/30"
      )}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                {gateResult.canExport ? (
                  <Package className="h-5 w-5 text-success" />
                ) : (
                  <Lock className="h-5 w-5 text-destructive" />
                )}
                Export Package
                {isDemoMode && (
                  <Badge variant="outline" className="ml-2 text-xs bg-warning/10 text-warning border-warning/30">
                    Demo — Illustrative Only
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                {gateResult.canExport 
                  ? 'All gates passed — ready to generate audit-ready export'
                  : 'Resolve blocking issues to enable export'
                }
              </CardDescription>
            </div>
            <div className="text-right">
              <Badge 
                variant={gateResult.canExport ? 'default' : 'destructive'} 
                className={cn(
                  "text-sm px-3 py-1",
                  gateResult.canExport && "bg-success hover:bg-success/90"
                )}
              >
                {gateResult.passedCount}/{gateResult.totalCount} gates
              </Badge>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {/* Demo Mode Banner */}
          {isDemoMode && (
            <Alert className="border-warning/50 bg-warning/10">
              <Sparkles className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning">Demo Mode — Illustrative Only</AlertTitle>
              <AlertDescription className="text-warning-foreground">
                This export preview uses simulated data. No claims will be submitted. 
                No PHI is stored or transmitted. Demo exports are clearly watermarked.
              </AlertDescription>
            </Alert>
          )}

          {/* Gate Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Gate Validation Progress</span>
              <span className="font-medium">
                {gateResult.passedCount} passed
                {gateResult.failedCount > 0 && `, ${gateResult.failedCount} blocked`}
                {gateResult.warningCount > 0 && `, ${gateResult.warningCount} warnings`}
              </span>
            </div>
            <Progress 
              value={gateProgress} 
              className={cn(
                "h-2",
                gateResult.failedCount > 0 && "[&>div]:bg-destructive"
              )} 
            />
          </div>

          {/* Blocking Failures */}
          {gateResult.blockingFailures.length > 0 && !isDemoMode && (
            <Alert variant="destructive">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>Export Blocked</AlertTitle>
              <AlertDescription className="mt-2">
                <ul className="space-y-2">
                  {gateResult.blockingFailures.map(gate => (
                    <li key={gate.id} className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-sm">{gate.failureDetail}</span>
                        {gate.quickAction && (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 ml-2 text-xs underline"
                            onClick={() => handleQuickAction(gate.quickAction!)}
                          >
                            Fix →
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {/* Demo mode: show blocking issues as informational */}
          {gateResult.blockingFailures.length > 0 && isDemoMode && (
            <Alert className="border-warning/30 bg-warning/5">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle className="text-warning">
                {gateResult.failedCount} gate(s) would block in production
              </AlertTitle>
              <AlertDescription className="text-xs text-muted-foreground mt-1">
                Demo mode bypasses hard gates for demonstration purposes.
              </AlertDescription>
            </Alert>
          )}

          {/* Gate Checklist (Collapsible) */}
          <Collapsible open={gateListOpen} onOpenChange={setGateListOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                <span className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4" />
                  Hard Gate Checklist ({blockingGates.length} checks)
                </span>
                {gateListOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-1 p-3 rounded bg-muted/30 max-h-64 overflow-y-auto">
                {blockingGates.map(gate => (
                  <GateCheckRow key={gate.id} gate={gate} onQuickAction={handleQuickAction} />
                ))}
                {auditGates.length > 0 && (
                  <>
                    <div className="text-xs font-medium text-muted-foreground pt-2 pb-1">
                      Audit Trail
                    </div>
                    {auditGates.map(gate => (
                      <GateCheckRow key={gate.id} gate={gate} onQuickAction={handleQuickAction} />
                    ))}
                  </>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Post-op state warning */}
          {!isPostOpStateValid && !isDemoMode && (
            <Alert variant="destructive">
              <Lock className="h-4 w-4" />
              <AlertTitle>Case Not Export Ready</AlertTitle>
              <AlertDescription>
                Case must complete reconciliation before export is available. 
                Current state: <strong>{postOpState || 'unknown'}</strong>
              </AlertDescription>
            </Alert>
          )}

          {/* Role-based view-only notice for surgeons */}
          {isViewOnly && (
            <Alert className="border-primary/30 bg-primary/5">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <AlertTitle>View Only</AlertTitle>
              <AlertDescription>
                Surgeons can review the export summary but cannot generate or download export packages.
                Contact LINK Ops to generate exports.
              </AlertDescription>
            </Alert>
          )}

          {/* Export Format Selection */}
          {(gateResult.canExport || isDemoMode) && isPostOpStateValid && (
            <>
              <div className="border-t pt-4">
                <Tabs value={selectedFormat} onValueChange={(v) => setSelectedFormat(v as typeof selectedFormat)}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="epic" className="gap-1">
                      <Copy className="h-4 w-4" />
                      Epic Copy/Paste
                    </TabsTrigger>
                    <TabsTrigger value="pdf" className="gap-1">
                      <FileText className="h-4 w-4" />
                      PDF Summary
                    </TabsTrigger>
                    <TabsTrigger value="json" className="gap-1">
                      <FileJson className="h-4 w-4" />
                      JSON
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="epic" className="mt-4">
                    <p className="text-sm text-muted-foreground mb-3">
                      Generate a clean, copyable text block with final CPT lines, modifiers, 
                      and auth reference for pasting into Epic.
                    </p>
                    {epicOutput && (
                      <div className="space-y-2">
                        <Textarea
                          value={epicOutput}
                          readOnly
                          className="font-mono text-xs min-h-[200px] bg-muted/50"
                        />
                        <Button variant="outline" size="sm" onClick={handleCopyEpic} className="gap-1">
                          <Copy className="h-3 w-3" />
                          Copy to Clipboard
                        </Button>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="pdf" className="mt-4">
                    <p className="text-sm text-muted-foreground">
                      Download a printable PDF summary including attestation, CPT reconciliation table, 
                      modifier rationale, and the complete audit timeline.
                    </p>
                  </TabsContent>

                  <TabsContent value="json" className="mt-4">
                    <p className="text-sm text-muted-foreground">
                      Download a structured JSON payload for system integration. 
                      Includes all case data, CPT lines, and audit trail.
                    </p>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Generate Button */}
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  This export does not constitute a claim submission.
                </p>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        onClick={handleGenerate}
                        disabled={isGenerating || (!gateResult.canExport && !isDemoMode) || isViewOnly}
                        className="gap-2"
                        size="lg"
                      >
                        {isGenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : selectedFormat === 'epic' ? (
                          <Copy className="h-4 w-4" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        {isDemoMode && <Sparkles className="h-4 w-4" />}
                        {selectedFormat === 'epic' 
                          ? 'Generate Copy/Paste Block' 
                          : selectedFormat === 'pdf' 
                            ? 'Download PDF' 
                            : 'Download JSON'
                        }
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isViewOnly && (
                    <TooltipContent>Surgeon role — export generation requires LINK Ops access</TooltipContent>
                  )}
                </Tooltip>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}

// ============================================================================
// GATE CHECK ROW SUBCOMPONENT
// ============================================================================

function GateCheckRow({ 
  gate, 
  onQuickAction 
}: { 
  gate: ExportGateCheck; 
  onQuickAction: (action: ExportGateQuickAction) => void;
}) {
  return (
    <div className={cn(
      "flex items-center gap-2 px-2 py-1.5 rounded text-sm",
      gate.status === 'passed' && "bg-success/5",
      gate.status === 'failed' && "bg-destructive/5",
      gate.status === 'warning' && "bg-warning/5",
    )}>
      {gate.status === 'passed' ? (
        <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
      ) : gate.status === 'failed' ? (
        <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
      )}
      
      <span className={cn(
        "flex-1 text-xs",
        gate.status === 'passed' ? "text-muted-foreground" : "text-foreground"
      )}>
        {gate.label}
      </span>

      {gate.quickAction && gate.status !== 'passed' && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => onQuickAction(gate.quickAction!)}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Fix this issue</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
